// ThreeJSAboutVideoAnimation.tsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import ResponsiveVideoWrapper from "./ResponsiveVideoWrapper";
import { DebugSettings } from "./types";

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: #08090a;
`;

export default function ThreeJSAboutVideoAnimation({ avifMode = false }: { avifMode?: boolean }) {
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [totalVideos, setTotalVideos] = useState(0);
  const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Create a ref to access the ThreeJSFrameViewer navigation functions
  const navigatorRef = useRef<{
    nextVideo: () => void;
    prevVideo: () => void;
    goToVideo: (index: number) => void;
  }>({
    nextVideo: () => {},
    prevVideo: () => {},
    goToVideo: () => {},
  });

  // Initialize settings with default values
  const [settings, setSettings] = useState<DebugSettings>({
    easingFunction: "velocityBased" as const,
    transitionDuration: 7000,
    adaptiveSpeed: true,
    maxDuration: 8000,
    autoplay: false,
    autoplayDelay: 5000,
    frameSkipRate: 4, // Default to render all frames (no skipping)
  });

  // Update handlers for each setting
  const updateSettings = useCallback((updates: Partial<DebugSettings>) => {
    setSettings((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateEasing = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      updateSettings({ easingFunction: e.target.value as DebugSettings["easingFunction"] });
    },
    [updateSettings],
  );

  const updateTransitionDuration = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateSettings({ transitionDuration: parseInt(e.target.value) });
    },
    [updateSettings],
  );

  const updateAdaptiveSpeed = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateSettings({ adaptiveSpeed: e.target.checked });
    },
    [updateSettings],
  );

  const updateMaxDuration = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateSettings({ maxDuration: parseInt(e.target.value) });
    },
    [updateSettings],
  );

  const updateAutoplay = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateSettings({ autoplay: e.target.checked });
    },
    [updateSettings],
  );

  const updateAutoplayDelay = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateSettings({ autoplayDelay: parseInt(e.target.value) });
    },
    [updateSettings],
  );

  const updateFrameSkipRate = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      updateSettings({ frameSkipRate: parseInt(e.target.value) });
    },
    [updateSettings],
  );

  // Function to navigate to the next video
  const goToNextVideo = useCallback(() => {
    if (navigatorRef.current) {
      navigatorRef.current.nextVideo();
    }
  }, [navigatorRef]);

  // Handle video navigation callback from ThreeJSFrameViewer
  const handleVideoChanged = useCallback((index: number, total: number) => {
    setCurrentVideoIndex(index);
    setTotalVideos(total);
  }, []);

  // Handle transition state callback from ThreeJSFrameViewer
  const handleTransitionChange = useCallback((transitioning: boolean) => {
    setIsTransitioning(transitioning);
  }, []);

  // Determine json and frames paths
  const jsonPath = avifMode
    ? "/assets/extracted_frames_24fps/video_frames_avif.json"
    : "/assets/extracted_frames_24fps/video_frames.json";

  const framesPath = avifMode
    ? "/assets/extracted_frames_24fps/avif"
    : "/assets/extracted_frames_24fps/avif/compressed_avif";

  // Autoplay timer effect
  useEffect(() => {
    // Clear any existing timer
    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }

    // Only set a new timer if autoplay is enabled and not currently transitioning
    if (settings.autoplay && !isTransitioning && totalVideos > 0) {
      autoplayTimerRef.current = setTimeout(() => {
        goToNextVideo();
      }, settings.autoplayDelay);
    }

    // Cleanup function
    return () => {
      if (autoplayTimerRef.current) {
        clearTimeout(autoplayTimerRef.current);
        autoplayTimerRef.current = null;
      }
    };
  }, [settings.autoplay, settings.autoplayDelay, isTransitioning, totalVideos, goToNextVideo]);

  return (
    <Container>
      <ResponsiveVideoWrapper
        jsonPath={jsonPath}
        framesPath={framesPath}
        transitionDuration={settings.transitionDuration}
        easingFunction={settings.easingFunction}
        adaptiveSpeed={settings.adaptiveSpeed}
        maxDuration={settings.maxDuration}
        frameSkipRate={settings.frameSkipRate}
        onVideoChanged={handleVideoChanged}
        onTransitionChange={handleTransitionChange}
        navigatorRef={navigatorRef}
      />
    </Container>
  );
}
